<template>
  <div class="registration-container">
    <div class="registration-form">
      <h1>{{ $t('Создайте аккаунт') }}</h1>
      <form @submit.prevent="handleRegister">
        <div class="form-group" :class="{ 'error-from-group': fieldErrors.fullname }">
          <input type="text" id="fullname" :placeholder="$t('Имя и фамилия')" v-model="form.fullname" class="input-field" />
          <p v-if="fieldErrors.fullname" class="input-error">{{ fieldErrors.fullname }}</p>
        </div>
        <div class="form-group" :class="{ 'error-from-group': fieldErrors.email }">
          <input id="email" v-model="form.email" :placeholder="$t('Почта')" class="input-field" />
          <p v-if="fieldErrors.email" class="input-error">{{ fieldErrors.email }}</p>
        </div>

        <!-- Поле для ввода телефона -->
        <div class="form-group phone-group" :class="{ 'error-from-group': fieldErrors.phone }">
          <div class="phone-input">
            <div class="country-select">
              <div class="selected-option" @click="toggleDropdown">
                <template v-if="selectedCountry.flag">
                  <img :src="selectedCountry.flag" alt="" class="flag" />
                  <span class="code"> &nbsp; &nbsp; (+{{ selectedCountry.code }})</span>
                </template>
                <template v-else>
                  <span class="globe-icon">🌍</span>
                  <span class="code"> &nbsp; +</span>
                  <input 
                    type="tel" 
                    v-model="customCountryCode" 
                    @click.stop 
                    :placeholder="$t('код')"
                    class="custom-code-input"
                    maxlength="4"
                  />
                </template>
              </div>
              <ul v-if="dropdownOpen" class="dropdown">
                <li v-for="country in countries" :key="country.name" @click="selectCountry(country)"
                  class="dropdown-item">
                  <template v-if="country.flag">
                    <img :src="country.flag" alt="" class="flag" />
                  </template>
                  <template v-else>
                    <span class="globe-icon">🌍</span>
                  </template>
                   <span class="name">{{ $t(country.name) }}</span>
                  <span class="code" v-if="country.code">(+{{ country.code }})</span>
                </li>
              </ul>
            </div>
            <input type="tel" id="phone" v-model="form.phone" :placeholder="phonePlaceholder" @input="formatPhoneNumber"
              class="input-field phone-number" />
          </div>
          <p v-if="fieldErrors.phone" class="input-error">{{ fieldErrors.phone }}</p>
        </div>
        <div class="form-group" :class="{ 'error-from-group': fieldErrors.username }">
          <input type="text" id="username" v-model="form.username" :placeholder="$t('Логин')" class="input-field" />
          <p v-if="fieldErrors.username" class="input-error">{{ fieldErrors.username }}</p>
        </div>
        <div class="form-group" :class="{ 'error-from-group': fieldErrors.password }">
          <div class="password-field">
            <input :type="showPassword ? 'text' : 'password'" id="password" v-model="form.password"
                   :placeholder="$t('Пароль')" class="input-field" />
            <span class="password-toggle" @click="showPassword = !showPassword">
              <img :src="showPassword ? eyeIcon : eyeIconOff" alt="Toggle password" class="eye-icon" />
            </span>
          </div>
          <p v-if="fieldErrors.password" class="input-error">{{ fieldErrors.password }}</p>
        </div>
        <div class="form-group" :class="{ 'error-from-group': fieldErrors.repeat_password }">
          <div class="password-field">
            <input :type="showRepeatPassword ? 'text' : 'password'" id="repeat_password" v-model="form.repeat_password"
                   :placeholder="$t('Повторите пароль')" class="input-field" />
            <span class="password-toggle" @click="showRepeatPassword = !showRepeatPassword">
      <img :src="showRepeatPassword ? eyeIcon : eyeIconOff" alt="Toggle password" class="eye-icon" />
    </span>
          </div>
          <p v-if="fieldErrors.repeat_password" class="input-error">{{ fieldErrors.repeat_password }}</p>
        </div>
        <div class="form-group promo-group">
          <div class="promo-toggle" @click="showPromoField = !showPromoField">
            <span class="promo-icon">🎁</span>
            <span>{{ showPromoField ? $t('Скрыть промо-код') : $t('У меня есть промо-код') }}</span>
          </div>
          <div v-if="showPromoField" class="promo-field-wrapper">
            <input 
              type="text" 
              v-model="form.promoCode" 
              :placeholder="$t('Введите промо-код')"
              class="input-field"
              :class="{ 'promo-valid': promoValidation.valid, 'promo-invalid': promoValidation.checked && !promoValidation.valid }"
            />
            <p v-if="promoValidation.message" class="promo-message" :class="{ 'success': promoValidation.valid, 'error': !promoValidation.valid }">
              {{ promoValidation.message }}
            </p>
          </div>
        </div>
        <div class="form-group checkbox-group" :class="{ 'error-from-group': fieldErrors.agreement }">
          <label>
            <input type="checkbox" v-model="form.agreement" class="checkbox" required />
            <span>{{ $t('Соглашаюсь с публичной офертой, политикой конфиденциальности и обработкой персональных данных.') }}</span>
          </label>
          <p v-if="fieldErrors.agreement" class="input-error">{{ fieldErrors.agreement }}</p>
        </div>
        <button type="submit" class="submit-button" :disabled="isSubmitting">
           {{ isSubmitting ? $t('Создание...') : $t('Создать') }}
        </button>
      </form>
      <div class="registration-info">
        <p>
          {{ $t('Уже зарегистрировались в сервисе?') }} <br />
          <span>
             <router-link :to="authPath('/login')">{{ $t('Войти в аккаунт') }}</router-link>
          </span>
        </p>
      </div>
    </div>
  </div>
</template>


<script>
import belarusIcon from '@/assets/images/auth/flags/icon-belarus.png'
import kyrgyzstanIcon from '@/assets/images/auth/flags/icon-kyrgyzstan.png'
import russiaIcon from '@/assets/images/auth/flags/icon-russia.png'
import thailandIcon from '@/assets/images/auth/flags/icon-thailand.png'
import ukraineIcon from '@/assets/images/auth/flags/icon-ukraine.png'
import eyeIcon from '@/assets/images/auth/eye_icon.svg'
import eyeIconOff from '@/assets/images/auth/eye_icon_off.svg'
import store from "@/store"
import { mapActions, mapState } from "vuex"
import { getSubscriptionPlans } from '@/api/subscription'
import { validatePromoCodePublic } from '@/api/ambassadors'
import { localizedAuthPath } from '@/utils/authRoutes'

export default {
  data() {
    return {
      form: {
        fullname: "",
        email: "",
        phone: "",
        username: "",
        password: "",
        repeat_password: "",
        agreement: false,
        promoCode: "",
      },
      selectedCountry: { name: "Россия", code: "7", flag: russiaIcon },
      customCountryCode: "",
      countries: [
        { name: "Россия", code: "7", flag: russiaIcon, pattern: /^(\d{3})(\d{3})(\d{2})(\d{2})$/, format: "($1) $2-$3-$4", maxLength: 10 },
        { name: "Украина", code: "380", flag: ukraineIcon, pattern: /^(\d{2})(\d{3})(\d{2})(\d{2})$/, format: "($1) $2-$3-$4", maxLength: 9 },
        { name: "Кыргызстан", code: "996", flag: kyrgyzstanIcon, pattern: /^(\d{3})(\d{2})(\d{2})(\d{2})$/, format: "$1 $2 $3 $4", maxLength: 9 },
        { name: "Беларусь", code: "375", flag: belarusIcon, pattern: /^(\d{2})(\d{3})(\d{2})(\d{2})$/, format: "($1) $2-$3-$4", maxLength: 9 },
        { name: "Тайланд", code: "66", flag: thailandIcon, pattern: /^(\d{1})(\d{4})(\d{4})$/, format: "$1 $2 $3", maxLength: 9 },
        { name: "Другая страна", code: "", flag: null, pattern: null, format: null, maxLength: 15 },
      ],
      fieldErrors: {},
      errorMessage: "",
      successMessage: "",
      dropdownOpen: false,
      showPassword: false,
      showRepeatPassword: false,
      showPromoField: false,
      isSubmitting: false,
      promoValidation: {
        checked: false,
        valid: false,
        message: "",
        promoData: null
      },
      eyeIcon,
      eyeIconOff,
      subscriptionPlans: [],
    }
  },
  async mounted() {
    await this.loadSubscriptionPlans();
  },
  computed: {
    ...mapState("auth", ["registrationSuccess"]),
    phonePlaceholder() {
      switch (this.selectedCountry.code) {
        case "7": return "(495) 231-36-60"
        case "380": return "(44) 278-13-57"
        case "996": return "312 97 61 61"
        case "375": return "(17) 327-45-62"
        case "66": return "8 1234 5678"
        default: return this.$t('Номер телефона')
      }
    },
    currentCountryConfig() {
      const config = this.countries.find(country => country.code === this.selectedCountry.code)
      if (!config) {
        return this.countries.find(country => country.name === "Другая страна")
      }
      return config
    },
    isCustomCountry() {
      return !this.selectedCountry.flag
    }
  },
  methods: {
    ...mapActions("auth", ["register"]),

    authPath(path) {
      return localizedAuthPath(this.$route, path)
    },
    
    formatPhoneNumber() {
      // Убираем все нецифровые символы
      let digits = this.form.phone.replace(/\D/g, "")
      
      // Ограничиваем длину согласно стране
      const config = this.currentCountryConfig
      const maxLength = config ? config.maxLength : 15
      if (digits.length > maxLength) {
        digits = digits.substring(0, maxLength)
      }
      
      // Применяем форматирование только если есть паттерн
      if (config && config.pattern && config.pattern.test(digits)) {
        this.form.phone = digits.replace(config.pattern, config.format)
      } else {
        this.form.phone = digits
      }
    },
    
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
    },
    
    selectCountry(country) {
      this.selectedCountry = country
      this.dropdownOpen = false
      // Сбрасываем номер при смене страны
      this.form.phone = ""
    },
    
    async handleRegister() {
      if (this.isSubmitting) return
      
      this.fieldErrors = {}
      this.errorMessage = ""
      this.successMessage = ""
      this.isSubmitting = true
      
      try {
        // Валидация промокода если введен
        if (this.form.promoCode.trim()) {
          try {
            const promoResult = await validatePromoCodePublic(this.form.promoCode.trim())
            if (!promoResult.valid) {
              this.promoValidation = { checked: true, valid: false, message: promoResult.message || this.$t('Промо-код недействителен'), promoData: null }
              this.isSubmitting = false
              return
            }
            this.promoValidation = { checked: true, valid: true, message: this.getPromoMessage(promoResult), promoData: promoResult }
          } catch (err) {
            this.promoValidation = { checked: true, valid: false, message: this.$t('Ошибка проверки промо-кода'), promoData: null }
            this.isSubmitting = false
            return
          }
        }
        
        // Подготавливаем телефон для отправки - убираем форматирование и добавляем код страны
        const phoneDigits = this.form.phone.replace(/\D/g, "")
        const countryCode = this.isCustomCountry ? this.customCountryCode : this.selectedCountry.code
        const phoneForApi = countryCode ? `+${countryCode}${phoneDigits}` : `+${phoneDigits}`
        
        // Создаем копию формы с правильным номером телефона и промокодом
        const formData = {
          ...this.form,
          phone: phoneForApi,
          promoCode: this.form.promoCode.trim() || undefined
        }
        
        const response = await this.register(formData)
        if (store.getters['auth/registrationSuccess']) {
          const pendingPlan = localStorage.getItem('pendingPlan')
          const pendingPlanPrice = localStorage.getItem('pendingPlanPrice')
          if (pendingPlan) {
            localStorage.removeItem('pendingPlan')
            localStorage.removeItem('pendingPlanPrice')
            await store.dispatch('auth/login', { email: this.form.email, password: this.form.password })
            if (store.getters['auth/loginSuccess']) {
              await store.dispatch('auth/loadUser')
              await store.dispatch('ui/initAvatarSrcFromUser')
              const price = pendingPlanPrice || this.getPlanPrice(pendingPlan)
              this.$router.push({ path: '/subscription/payment', query: { plan: pendingPlan, price } })
              return
            }
          }
          this.$router.push(this.authPath('/success-register'))
        } else {
          this.errorMessage = this.$t(response.errors.title)
          if (response.errors.details) {
            Object.keys(response.errors.details).forEach((field) => {
              this.fieldErrors[field] = this.$t(response.errors.details[field][0])
            })
          }
        }
      } finally {
        this.isSubmitting = false
      }
    },
    
    getPromoMessage(promoData) {
      if (promoData.type === 'trial') {
        return this.$t('dynamic.trialPromo', { days: promoData.trial_days, plan: promoData.trial_plan })
      } else if (promoData.type === 'discount') {
        return this.$t('dynamic.discountPromo', { percent: promoData.discount_percent })
      }
      return this.$t('Промо-код действителен')
    },
    async loadSubscriptionPlans() {
      try {
        this.subscriptionPlans = await getSubscriptionPlans();
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        this.subscriptionPlans = [];
      }
    },
    getPlanPrice(plan) {
      const planData = this.subscriptionPlans.find(p => p.id === plan);
      return planData?.price || 0;
    },
  },
}
</script>


<style scoped>
img.flag {
  width: 28px;
}

.country-select {
  display: flex;
  height: 50px;
  font-size: 16px;
  border: none !important;
  border-radius: 8px 0 0 8px;
  padding: 10px 0 10px 18px;
  border-right: none;
  width: 130px;
  box-shadow: 3px 1px 4px 0px #12223733 inset !important;
  background-color: #fff;
  color: #496782;
  font-family: 'Montserrat';
  font-weight: 100;
  margin-top: 8px;
}

.globe-icon {
  font-size: 20px;
  line-height: 1;
}

.custom-code-input {
  width: 45px;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #496782;
  font-family: 'Montserrat';
  padding: 0;
  outline: none;
}

.custom-code-input::placeholder {
  color: #a0aec0;
}

.country-select .name {
  width: 100px;
  margin-left: 10px;
  font-family: 'Montserrat';
  font-weight: 100;
}

.country-select .code {
  float: right;
  font-family: 'Montserrat';
  font-weight: 100;
}

.country-select ul.dropdown li.dropdown-item {
  display: flex;
  align-content: flex-end;
  align-items: center;
  margin-bottom: 10px;
}

.country-select ul.dropdown {
  margin-top: 10px;
  padding: 20px 20px 10px 20px;
  position: absolute;
  z-index: 99999;
  background-color: #fff;
  width: 100%;
  list-style-type: none;
  left: 0;
  border-radius: 10px;
  top: 50px;
}

.selected-option {
  display: flex;
  text-align: center;
  line-height: 2em;
  align-items: center;
}

.phone-input {
  display: flex;
  align-items: center;
}

.phone-input .input-field {
  border-radius: 0 8px 8px 0;
  border-left: none;
  border: none !important;
  box-shadow: none;
  box-shadow: -3px 1px 4px 0px #12223733 inset;
}

.phone-number {
  flex: 1;
  border-radius: 0 8px 8px 0;
  border: 1px solid #ddd;
  padding: 10px;
  font-size: 16px;
}

.flag-icon {
  width: 20px;
  height: 15px;
  margin-right: 5px;
}

select.country-select {
  margin-top: 8px;
  box-shadow: 1px 1px 4px 0px #12223733 inset;
  border: 1px solid #ddd;
  font-family: 'Montserrat';
  font-weight: 100;
  font-size: 16px;
  border: 1px solid #ddd;
}

.input-error {
  color: red;
  font-size: 12px;
  margin-top: 4px;
  position: absolute;
}

.registration-container {
  background: linear-gradient(to bottom, #FAF9F7, #BBCAD8);
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.registration-info {
  text-align: center;
  margin-top: 20px;
}

.registration-info p {
  font-family: 'Montserrat';
  font-weight: 100;
}

.registration-info p span {
  font-family: 'Montserrat';
  font-weight: 900;
  font-size: 16px;
}

h1 {
  margin-bottom: 20px;
  font-family: 'Montserrat';
  font-weight: 900;
  text-align: center;
}

.registration-form {
  width: 100%;
  max-width: 400px;
  padding: 20px;
}

.input-field {
  height: 50px;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 13px;
  box-sizing: border-box;
  margin-top: 8px;
  box-shadow: 1px 1px 4px 0px #12223733 inset;
  font-family: 'Montserrat';
  font-weight: 100;
}

::placeholder {
  color: #BBCAD8;
  opacity: 1;
}

::-ms-input-placeholder {
  color: #BBCAD8;
}

.checkbox-group span {
  color: #496782;
  font-family: 'Montserrat';
  font-weight: 100;
  display: inline-block;
  width: 93%;
  margin-left: 4px;
  font-size: 12px;
  line-height: 1.4;
}


input.checkbox {
  display: inline-block;
  margin: 0;
  height: 25px;
  width: 27px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  cursor: pointer;
}

input.checkbox::before {
  content: "";
  position: absolute;
  top: 3px;
  left: 0;
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 3px;
  box-shadow:
    inset 1px 1px 2px rgba(0, 0, 0, 0.25),
    inset -1px -1px 2px rgba(0, 0, 0, 0.1);
}

input.checkbox:checked::after {
  content: "";
  position: absolute;
  top: 34%;
  left: 55%;
  width: 22px;
  height: 10.4px;
  border-left: 4px solid #496782;
  border-bottom: 4px solid #496782;
  border-radius: 2px;
  transform: translate(-50%, -50%) rotate(305deg);
  transform-origin: center center;
}

.form-group.checkbox-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 15px;
  height: auto;
}

.checkbox-group label {
  display: flex;
  align-items: center;
}

.checkbox-group {
  margin-top: 10px;
}

.form-group.phone-group {
  margin-bottom: 15px;
}

.checkbox {
  margin-right: 10px;
}

.submit-button {
  width: 100%;
  padding: 14px;
  font-size: 16px;
  background-color: #496782;
  color: white;
  border: none;
  border-radius: 8px;
  margin-top: 10px;
  cursor: pointer;
  transition: background-color 0.3s;
  box-shadow: -5px -3px 8px 0px #FFFFFF;
}

.submit-button:hover {
  background-color: #496782;
}

.error {
  color: red;
  margin-top: 10px;
}

.success {
  color: green;
  margin-top: 10px;
}

.form-group.error-from-group::after {
  content: '!';
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 16px;
  height: 16px;
  background-color: #B60000;
  color: #FFFFFF;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  left: 94%;
  top: -33px;
}

p.input-error {
  color: #000 !important;
  background-color: #fff;
  box-shadow: 0px 0px 10px 0px #B2B0AD66;
  padding: 10px;
  border-radius: 10px;
  position: absolute;
  right: 0;
  z-index: 1;
  top: 40px;
}

.form-group {
  height: 50px;
  margin-top: 5px;
  position: relative;
}

a {
  text-decoration: none !important;
  font-weight: 100;
  color: #496782;
}
.password-field {
  position: relative;
}

.password-toggle {
  position: absolute;
  top: 60%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 20px; /* можно чуть шире, чем у тебя было */
  right: 15px;
}

.icon-eye,
.icon-eye-off {
  font-size: 20px;
  color: #888;
}

.promo-group {
  height: auto !important;
  margin-top: 15px !important;
  margin-bottom: 10px;
}

.promo-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #496782;
  font-family: 'Montserrat';
  font-weight: 500;
  font-size: 14px;
  padding: 8px 0;
  transition: opacity 0.2s;
}

.promo-toggle:hover {
  opacity: 0.8;
}

.promo-icon {
  font-size: 18px;
}

.promo-field-wrapper {
  margin-top: 8px;
}

.promo-field-wrapper .input-field {
  margin-top: 0;
}

.promo-field-wrapper .input-field.promo-valid {
  border: 2px solid #28a745 !important;
  box-shadow: 0 0 4px rgba(40, 167, 69, 0.3) !important;
}

.promo-field-wrapper .input-field.promo-invalid {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 4px rgba(220, 53, 69, 0.3) !important;
}

.promo-message {
  margin-top: 6px;
  font-size: 13px;
  font-family: 'Montserrat';
}

.promo-message.success {
  color: #28a745;
}

.promo-message.error {
  color: #dc3545;
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

</style>
